import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import DOMPurify from "dompurify";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Undo2, Redo2 } from "lucide-react";

// Allowlist mirrors the backend nh3 config. No <script>, no inline handlers, no <iframe>.
const SANITIZE_OPTS = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "b", "em", "i", "u", "s", "strike",
    "h1", "h2", "h3", "h4", "ul", "ol", "li", "a", "blockquote", "span", "hr",
  ],
  ALLOWED_ATTR: ["href", "title", "target", "rel", "class"],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|\/|#)/i,
  FORBID_TAGS: ["style", "script", "iframe"],
};

export const sanitize = (html) => DOMPurify.sanitize(html || "", SANITIZE_OPTS);

// Wrap existing plain-text copy into safe HTML so it migrates cleanly into the rich editor.
export const plainToHtml = (text) => {
  if (!text || !String(text).trim()) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return String(text)
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

// Renders sanitized rich-text on the public site. Hard returns (<p>) and soft
// returns (<br>) display correctly via the .mmm-rte styles in index.css.
export const RichTextView = ({ html, className = "", testid }) => {
  if (!html || !String(html).trim()) return null;
  return (
    <div
      data-testid={testid}
      className={`mmm-rte ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitize(html) }}
    />
  );
};

const Btn = ({ active, onClick, title, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    title={title}
    aria-label={title}
    aria-pressed={!!active}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors ${
      active ? "bg-[#1C4358] text-white" : "text-[#1C4358] hover:bg-[#1C4358]/10"
    }`}
  >
    {children}
  </button>
);

export const RichText = ({ value, onChange, placeholder = "Write here…", testid = "richtext" }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] }, link: false }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class: "tiptap mmm-rte min-h-[130px] px-3 py-2.5 focus:outline-none",
        "data-testid": `${testid}-area`,
      },
    },
  });

  // Sync editor when the value changes from outside (load / revert / switching items)
  useEffect(() => {
    if (!editor) return;
    const cur = editor.getHTML();
    const curNorm = cur === "<p></p>" ? "" : cur;
    if ((value || "") !== curNorm) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (https://…, mailto:, tel:, or /path)", prev);
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  return (
    <div data-testid={testid} className="rounded-lg border border-[#1C4358]/20 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[#1C4358]/12 bg-[#F7FAFC] px-1.5 py-1" role="toolbar" aria-label="Text formatting">
        <Btn testid={`${testid}-bold`} title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></Btn>
        <Btn testid={`${testid}-italic`} title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></Btn>
        <span className="w-px h-5 bg-[#1C4358]/12 mx-1" />
        <Btn testid={`${testid}-h2`} title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={15} /></Btn>
        <Btn testid={`${testid}-h3`} title="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={15} /></Btn>
        <span className="w-px h-5 bg-[#1C4358]/12 mx-1" />
        <Btn testid={`${testid}-ul`} title="Bulleted list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></Btn>
        <Btn testid={`${testid}-ol`} title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></Btn>
        <Btn testid={`${testid}-link`} title="Link" active={editor.isActive("link")} onClick={setLink}><Link2 size={15} /></Btn>
        <span className="w-px h-5 bg-[#1C4358]/12 mx-1" />
        <Btn testid={`${testid}-undo`} title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo2 size={15} /></Btn>
        <Btn testid={`${testid}-redo`} title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo2 size={15} /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichText;
